import i18n from 'i18next';

import { Languages } from './languages';

const supportedLanguages = Object.values(Languages) as string[];

/**
 * Resolves an app language code to a BCP-47 locale for Intl formatting.
 * Region subtags are normalized to their base language (e.g. nb-NO -> nb), since
 * the language detector may surface a full tag. Falls back to English when the
 * language is missing or unsupported.
 */
export const resolveLocale = (language?: string | null): string => {
  const base = language?.split('-')[0] ?? '';
  return supportedLanguages.includes(base) ? base : Languages.English;
};

export type NumberFormatters = {
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatInteger: (value: number) => string;
};

/** Builds locale-bound number formatters. Locale must already be a valid BCP-47 tag. */
export const createNumberFormatters = (locale: string): NumberFormatters => ({
  formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
  formatInteger: value => new Intl.NumberFormat(locale).format(Math.round(value)),
});

const currentLocale = (): string => resolveLocale(i18n.language);

/**
 * Formats a number using the currently selected language.
 * For use outside React render; inside components prefer the useNumberFormat hook
 * so the value re-formats when the language changes.
 */
export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string =>
  createNumberFormatters(currentLocale()).formatNumber(value, options);

/** Formats a value rounded to the nearest integer using the currently selected language. */
export const formatInteger = (value: number): string =>
  createNumberFormatters(currentLocale()).formatInteger(value);
